import { MAX_WIND, type TerrainStyle } from '@tanky/shared';

interface Leaf {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  spin: number;
  phase: number;
  size: number;
  color: string;
  depth: number;
}

const LEAF_COLORS = ['#8bc34a', '#c0ca33', '#fbc02d', '#ff9800', '#e65100', '#a1887f', '#d84315'];
const DUST_COLORS = ['rgba(220,210,240,0.7)', 'rgba(190,180,220,0.6)', 'rgba(255,255,255,0.5)'];
const MARS_DUST = ['rgba(230,150,100,0.7)', 'rgba(200,110,70,0.6)', 'rgba(250,190,140,0.5)'];
const PARALLAX = 0.3;

const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Listí (na jeskynní mapě prach) letící po větru na pozadí.
 * Žije v obrazovkových souřadnicích, s kamerou se posouvá jen částečně (paralaxa).
 */
export class Leaves {
  private leaves: Leaf[] = [];
  private lastCam: { x: number; y: number } | null = null;

  constructor(private style: TerrainStyle) {}

  private target(wind: number) {
    // Na Měsíci nic nelétá (bez atmosféry)
    if (this.style === 'moon') return 0;
    const n = 8 + (45 * Math.abs(wind)) / MAX_WIND;
    return Math.round(reducedMotion ? n / 2 : n);
  }

  private spawn(vw: number, vh: number, wind: number, anywhere: boolean): Leaf {
    const depth = 0.5 + Math.random() * 0.5;
    const speed = wind * 4 * depth;
    let x: number;
    let y: number;
    if (anywhere) {
      x = Math.random() * vw;
      y = Math.random() * vh * 0.85;
    } else if (Math.abs(wind) < 8) {
      // Bezvětří: padá shora
      x = Math.random() * vw;
      y = -10;
    } else {
      // Vítr: přilétá z návětrné strany
      x = wind > 0 ? -12 : vw + 12;
      y = Math.random() * vh * 0.8;
    }
    const dust = this.style !== 'hills';
    const palette = this.style === 'mars' ? MARS_DUST : dust ? DUST_COLORS : LEAF_COLORS;
    return {
      x,
      y,
      vx: speed + (Math.random() - 0.5) * 12,
      vy: 10 + Math.random() * 18,
      rot: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * (2 + Math.abs(wind) / 12),
      phase: Math.random() * Math.PI * 2,
      size: (dust ? 1.8 + Math.random() * 2.2 : 5 + Math.random() * 4) * depth,
      color: palette[Math.floor(Math.random() * palette.length)],
      depth,
    };
  }

  update(dt: number, vw: number, vh: number, wind: number, cam: { x: number; y: number; zoom: number }) {
    // Paralaxa: posun kamery posune listí jen o část
    if (this.lastCam) {
      const dx = (cam.x - this.lastCam.x) * cam.zoom * PARALLAX;
      const dy = (cam.y - this.lastCam.y) * cam.zoom * PARALLAX;
      for (const l of this.leaves) {
        l.x -= dx * l.depth;
        l.y -= dy * l.depth;
      }
    }
    this.lastCam = { x: cam.x, y: cam.y };

    const target = this.target(wind);
    if (!this.leaves.length) for (let i = 0; i < target; i++) this.leaves.push(this.spawn(vw, vh, wind, true));
    else if (this.leaves.length < target && Math.random() < dt * 8) this.leaves.push(this.spawn(vw, vh, wind, false));

    const gust = 1 + Math.sin(performance.now() / 1300) * 0.25;
    for (const l of this.leaves) {
      // Plynule se přizpůsobit aktuálnímu větru
      const want = wind * 4 * l.depth * gust;
      l.vx += (want - l.vx) * Math.min(1, dt * 0.8);
      l.phase += dt * (1.5 + Math.abs(wind) / 25);
      l.rot += l.spin * dt;
      l.x += (l.vx + Math.sin(l.phase) * 10) * dt;
      l.y += (l.vy * (0.6 + 0.4 * Math.cos(l.phase * 0.7))) * dt;
    }
    // Pryč z obrazovky → zaniknout (a pokud je listí moc, už nevznikne nové)
    this.leaves = this.leaves.filter((l) => l.x > -30 && l.x < vw + 30 && l.y < vh + 20);
    if (this.leaves.length > target + 5) this.leaves.length = target + 5;
  }

  draw(ctx: CanvasRenderingContext2D) {
    const dust = this.style !== 'hills';
    for (const l of this.leaves) {
      ctx.globalAlpha = 0.55 + l.depth * 0.4;
      ctx.fillStyle = l.color;
      if (dust) {
        ctx.beginPath();
        ctx.arc(l.x, l.y, l.size, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.save();
      ctx.translate(l.x, l.y);
      ctx.rotate(l.rot);
      // Kmitání „naplocho“ – zúžení podle natočení
      ctx.scale(1, 0.35 + 0.65 * Math.abs(Math.sin(l.phase)));
      const s = l.size;
      ctx.beginPath();
      ctx.moveTo(-s, 0);
      ctx.quadraticCurveTo(0, -s * 0.8, s, 0);
      ctx.quadraticCurveTo(0, s * 0.8, -s, 0);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.25)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(-s * 1.2, 0);
      ctx.lineTo(s * 0.8, 0);
      ctx.stroke();
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
}
